import ClientAppCard from '@/components/client/client-app-card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import type { IClient, IInstalledApp } from '@/entities/client.entity';
import { $appendClientDetailApps, $clientDetailApps, $setClientDetailApps } from '@/entities/client.entity';
import { SOCKET_EVENTS } from '@/api/socket-events';
import { useClientVirtualFeed } from '@/hooks/use-client-virtual-feed';
import { getMockAppsForClient, paginateMockItems } from '@/mocks/fixtures';
import { useUnit } from 'effector-react';
import { LayoutGridIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

interface ClientAppsModalProps {
	client: IClient;
	onCopy: (text: string) => void;
}

export default function ClientAppsModal({ client, onCopy }: ClientAppsModalProps) {
	const [apps, setApps, appendApps] = useUnit([$clientDetailApps, $setClientDetailApps, $appendClientDetailApps]);
	const [isOpen, setIsOpen] = useState(false);
	const [searchText, setSearchText] = useState('');

	const appsCount = client.appsCount ?? 0;

	const { listRef, content, isSearching } = useClientVirtualFeed<IInstalledApp>({
		clientId: client.id,
		searchText,
		enabled: isOpen,
		fetchEvent: SOCKET_EVENTS.getClientApps,
		responseEvent: SOCKET_EVENTS.addClientApps,
		items: apps,
		setItems: setApps,
		appendItems: appendApps,
		getItemId: item => item.id,
		estimateSize: 56,
		emptyLabel: 'Приложения не получены',
		renderItem: app => <ClientAppCard app={app} onCopy={onCopy} />,
		devMockFetch: ({ cursor, searchText: q }) => {
			const page = paginateMockItems(
				getMockAppsForClient(),
				{ cursor, searchText: q },
				(item, query) =>
					item.label.toLowerCase().includes(query) || item.packageName.toLowerCase().includes(query),
			);
			return { clientId: client.id, ...page };
		},
	});

	function handleOpenChange(open: boolean) {
		setIsOpen(open);
		if (!open) {
			setSearchText('');
			setApps([]);
		}
	}

	useEffect(() => {
		if (!isOpen) setApps([]);
	}, [isOpen, setApps]);

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button variant='outline' size='sm' className='h-8 text-xs w-full'>
					<LayoutGridIcon size={14} />
					Приложения{appsCount > 0 ? ` (${appsCount})` : ''}
				</Button>
			</DialogTrigger>
			<DialogContent className='sm:max-w-[480px] w-full max-h-[85vh] flex flex-col overflow-hidden p-0 gap-0'>
				<DialogHeader className='px-4 pt-4 pb-2 shrink-0'>
					<DialogTitle className='text-left'>
						Установленные приложения
						{appsCount > 0 ? (
							<span className='ml-1.5 text-muted-foreground font-normal tabular-nums'>· {appsCount}</span>
						) : null}
					</DialogTitle>
				</DialogHeader>

				<div className='px-4 pb-2 shrink-0 min-w-0'>
					<Input
						className='w-full min-w-0'
						placeholder='Поиск...'
						value={searchText}
						onChange={e => setSearchText(e.target.value)}
					/>
					{isSearching ? (
						<p className='mt-1.5 text-xs text-muted-foreground tabular-nums'>Найдено: {apps.length}</p>
					) : null}
				</div>

				<div ref={listRef} className='flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 pb-4'>
					{content}
				</div>
			</DialogContent>
		</Dialog>
	);
}
